import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { useMyFleet, useRenameIsland } from '@/api/hooks'
import { useAuth } from '@/auth/useAuth'
import { useMercureSubscription, type MercureEventData } from '@/api/mercure'
import { ResourceBar } from '@/components/ResourceBar'
import { useSetStickyResources } from '@/layouts/stickyResources'
import { BuildingCard } from '@/components/BuildingCard'
import { BuildTavernCard } from '@/components/BuildTavernCard'
import { ConstructionSlots } from '@/components/ConstructionSlots'
import { DefensePanel } from '@/components/DefensePanel'
import { EditableName } from '@/components/EditableName'
import { IslandIcon, BuildingsIcon, FortIcon } from '@/components/game/GameIcons'
import { SeaScene } from '@/components/game/SeaScene'

export function Island() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const renameIsland = useRenameIsland()
  const { data, isLoading, isError, error } = useMyFleet()
  const { hash } = useLocation()

  // Live refresh when a building upgrade or NPC raid resolves server-side (Mercure).
  const handleMercure = useCallback(
    (event: MercureEventData) => {
      if (event.event === 'building_upgraded') {
        qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
        toast.success('🏗️ Amélioration terminée !', { duration: 5000 })
      } else if (event.event === 'npc_raid_resolved') {
        qc.invalidateQueries({ queryKey: ['me', 'defense'] })
        qc.invalidateQueries({ queryKey: ['me', 'fleet'] })
        const repelled = event.outcome === 'repelled'
        toast(repelled ? '🏰 Attaque pirate repoussée !' : '☠️ Ton île a été pillée !', {
          icon: repelled ? '🛡️' : '⚔️',
          duration: 6000,
        })
      }
    },
    [qc],
  )
  useMercureSubscription(
    user ? [`/users/${user.id}/buildings`, `/users/${user.id}/raids`] : [],
    handleMercure,
    !!user,
  )

  // Mobile: once the full ResourceBar scrolls under the header, ask the layout to
  // reveal its resource mini-banner inside the sticky header (flush, exactly like
  // the other pages) rather than floating a detached overlay.
  const resourcesRef = useRef<HTMLDivElement>(null)
  const setStickyResources = useSetStickyResources()
  useEffect(() => () => setStickyResources(false), [setStickyResources]) // reset on leaving the page
  useEffect(() => {
    const el = resourcesRef.current
    if (!el) {
      setStickyResources(false)

      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => setStickyResources(!entry.isIntersecting),
      { rootMargin: '-64px 0px 0px 0px' }, // the sticky header height (h-16)
    )
    observer.observe(el)

    return () => observer.disconnect()
  }, [data?.resources, setStickyResources])

  // Deep-link from the Carte attack panel: arriving with #defense scrolls to the
  // Défenses section once the page has loaded (React Router doesn't scroll to
  // hashes on its own — cf. Legal.tsx ; #incoming-pvp is handled inside DefensePanel).
  useEffect(() => {
    if (isLoading || hash !== '#defense') return
    document.getElementById('defense')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [hash, isLoading])

  if (isLoading) {
    return <div className="max-w-5xl mx-auto p-6 text-ocean-100">Chargement de l'île…</div>
  }

  if (isError) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-rum-500">Impossible de charger l'île : {(error as Error).message}</p>
      </div>
    )
  }

  if (!data?.island || !data.resources) {
    return (
      <div className="max-w-5xl mx-auto p-6">
        <p className="text-ocean-300 italic">Aucune île, étrange pour un capitaine.</p>
      </div>
    )
  }

  const resources = data.resources
  const hqLevel = data.buildings.find((b) => b.type === 'hq')?.level ?? 1
  // Créneaux de chantier : le serveur est la source de vérité (le QG les ouvre).
  // Repli sur un créneau unique si le payload est ancien, jamais sur zéro, ce qui
  // bloquerait toute construction.
  const slots = data.constructionSlots?.total ?? 1
  const slotsUsed = data.constructionSlots?.used ?? data.buildings.filter((b) => b.isUpgrading).length

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-[300px] space-y-6">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl text-sand-300 flex items-center gap-3">
          <IslandIcon className="w-8 h-8 text-sand-300" aria-hidden="true" />
          <EditableName
            value={data.island.name}
            onSave={(name) => renameIsland.mutateAsync({ islandId: data.island!.id, name })}
            inputClassName="font-display text-3xl sm:text-4xl text-sand-100"
          />
        </h1>
        <p className="text-sm text-ocean-200">
          {data.island.typeLabel} · Position ({data.island.x}, {data.island.y}) · QG niveau {data.island.qgLevel}
        </p>
      </div>

      <section>
        <h2 className="font-display text-lg text-sand-100 mb-2">Ressources</h2>
        <div ref={resourcesRef}>
          <ResourceBar resources={resources} />
        </div>
        <p className="text-xs text-ocean-300 mt-1">
          Les ressources s'accumulent en temps réel. Améliore le Quartier-Général pour débloquer les niveaux supérieurs des autres bâtiments.
        </p>
      </section>

      <section>
        <h2 className="font-display text-lg text-sand-100 mb-3 flex items-center gap-x-3 gap-y-1 flex-wrap">
          <span className="flex items-center gap-2">
            <BuildingsIcon className="w-5 h-5 text-sand-300" aria-hidden="true" /> Bâtiments ({data.buildings.length})
          </span>
          <ConstructionSlots used={slotsUsed} total={slots} />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.buildings.map((b) => (
            <BuildingCard
              key={b.id}
              building={b}
              resources={resources}
              hqLevel={hqLevel}
              slotsUsed={slotsUsed}
              slots={slots}
            />
          ))}
          {data.buildings.every((b) => b.type !== 'tavern') && (
            <BuildTavernCard
              slotsUsed={slotsUsed}
              slots={slots}
              premiumMultiplier={data.constructionSlots?.nextPremiumMultiplier ?? 1}
            />
          )}
        </div>
      </section>

      <section id="defense" className="scroll-mt-24">
        <h2 className="font-display text-lg text-sand-100 mb-3 flex items-center gap-2">
          <FortIcon className="w-5 h-5 text-sand-300" aria-hidden="true" /> Défenses
        </h2>
        <DefensePanel resources={resources} />
      </section>

      {/* Integrated maritime backdrop — a fleet at sail, always present at the foot of the page. */}
      <SeaScene scene="map" />
    </div>
  )
}
